/**
 * Tabs Component
 *
 * Light DOM Web Component for the felix/tabs block. Builds an accessible tab
 * nav from the labelled panels it wraps, then shows one panel at a time.
 *
 * Markup (server-rendered by blocks/tabs + blocks/tab):
 *   <felix-tabs class="tabs">
 *     <div class="tabs__panels">
 *       <div class="tabs__panel" data-tab-label="Treatments">…</div>
 *       <div class="tabs__panel" data-tab-label="Medications">…</div>
 *     </div>
 *   </felix-tabs>
 *
 * Progressive enhancement: with JS off, every panel stays visible (no nav, no
 * .hidden toggling). Once connected, a role="tablist" nav is injected before
 * the panels and all but the active panel get the .hidden utility.
 *
 * Keyboard: Left/Up + Right/Down cycle, Home/End jump to ends (roving tabindex).
 *
 * @package Felix
 */

class FelixTabs extends HTMLElement {
	connectedCallback() {
		this.panelsWrap = this.querySelector('.tabs__panels')
		if (!this.panelsWrap) {
			return
		}

		this.panels = Array.from(this.panelsWrap.children).filter((child) => child.classList.contains('tabs__panel'))
		if (this.panels.length < 2) {
			return
		}

		this.nav = document.createElement('div')
		this.nav.className = 'tabs__nav container--large flex flex-wrap gap-24'
		this.nav.setAttribute('role', 'tablist')

		this.triggers = this.panels.map((panel, index) => {
			if (!panel.id) {
				panel.id = `tabs-panel-${Math.random().toString(36).slice(2, 9)}`
			}
			const label = panel.getAttribute('data-tab-label') || `Tab ${index + 1}`

			const trigger = document.createElement('button')
			trigger.type = 'button'
			trigger.className = 'tabs__trigger text-heading-4'
			trigger.id = `${panel.id}-tab`
			trigger.textContent = label
			trigger.setAttribute('role', 'tab')
			trigger.setAttribute('aria-controls', panel.id)

			panel.setAttribute('role', 'tabpanel')
			panel.setAttribute('aria-labelledby', trigger.id)
			panel.setAttribute('tabindex', '0')

			trigger.addEventListener('click', () => this.activate(index))
			trigger.addEventListener('keydown', (event) => this.onKeydown(event, index))

			this.nav.appendChild(trigger)
			return trigger
		})

		this.panelsWrap.parentNode.insertBefore(this.nav, this.panelsWrap)
		this.classList.add('tabs--ready')
		this.activate(0)
	}

	activate(index) {
		this.panels.forEach((panel, i) => {
			const isActive = i === index
			panel.classList.toggle('hidden', !isActive)
			panel.classList.toggle('tabs__panel--active', isActive)

			const trigger = this.triggers[i]
			trigger.setAttribute('aria-selected', isActive ? 'true' : 'false')
			trigger.tabIndex = isActive ? 0 : -1
			trigger.classList.toggle('text-slate-20', isActive)
			trigger.classList.toggle('text-slate-40', !isActive)
		})
		this.activeIndex = index
	}

	onKeydown(event, index) {
		const last = this.triggers.length - 1
		let next = null

		switch (event.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				next = index === last ? 0 : index + 1
				break
			case 'ArrowLeft':
			case 'ArrowUp':
				next = index === 0 ? last : index - 1
				break
			case 'Home':
				next = 0
				break
			case 'End':
				next = last
				break
			default:
				return
		}

		event.preventDefault()
		this.activate(next)
		this.triggers[next].focus()
	}
}

customElements.define('felix-tabs', FelixTabs)
